/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import usePermissions from 'hooks/usePermissions'
import * as React from 'react'

import useIntegration from '@/hooks/useIntegration'
import { FormBuilderContext } from '@/pages/Contexts/FormBuilderContext'
import Ui from '@/ui'
import Space from '@/ui/atoms/Space'
import TextField from '@/ui/molecules/TextField'
import ToggleField from '@/ui/molecules/ToggleField'
import useQuestionIntegration from '@/utils/domain/FormBuilder/useQuestionIntegration'
import useBuilderQuestion from '@/utils/FormBuilder/hooks/useBuilderQuestion'

function BuilderSideMenuIntegration(): React.ReactNode {
  const { selectedItem, setQuestion } = React.useContext(FormBuilderContext)
  const {
    integration,
    integrationOptions,
    integrationTitledName,
    getIntegrationOptionsValue,
  } = useIntegration()
  const { makeQuestionIntegration } = useQuestionIntegration()

  const question = useBuilderQuestion(selectedItem.id)
  const questionIntegrationField =
    question?.[`${integration ?? 'elation'}_integration`]
  const questionType = question?.question_type?.question_type
  const questionSubtype = question?.question_type?.question_subtype

  const questionIntegration = React.useMemo(
    () =>
      questionIntegrationField ||
      makeQuestionIntegration({
        question: question?.id,
        isNew: true,
      }),
    [makeQuestionIntegration, question?.id, questionIntegrationField]
  )

  const shouldShowLabel = React.useMemo(() => {
    if (!questionIntegration) return false

    if (
      questionIntegration.type === 'Patient Demographics' &&
      questionIntegration.field !== 'patients.notes'
    ) {
      return false
    }

    return ![
      'Guarantor',
      'Primary Insurance',
      'Primary Insurance Policy Holder',
      'Secondary Insurance',
      'Secondary Insurance Policy Holder',
      'Emergency Contact',
      'Reports',
      'Fax Inbox',
    ].includes(questionIntegration.type ?? '')

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionIntegration, questionIntegration.type, questionIntegration.field])

  React.useEffect(() => {
    // @ts-ignore
    if (!questionIntegration || !questionIntegration?.hasChanged) return

    if (
      !questionIntegration.type &&
      !questionIntegration.field &&
      !questionIntegration.is_list &&
      !questionIntegration.field_label
    ) {
      // @ts-ignore
      setQuestion(question.id, {
        ...question,
        [`${integration}_integration`]: null,
      })
    }
  }, [integration, question, questionIntegration, setQuestion])

  const onSelectIntegrationType = React.useCallback(
    (selectedOption: string) => {
      questionIntegration.type = selectedOption
      questionIntegration.field = ''
      // @ts-ignore
      questionIntegration.hasChanged = true

      // @ts-ignore
      setQuestion(question.id, {
        ...question,
        [`${integration}_integration`]: questionIntegration,
      })
    },
    [integration, question, questionIntegration, setQuestion]
  )

  const onUpdateIntegrationLabel = React.useCallback(
    (value: string) => {
      questionIntegration.field_label = value
      // @ts-ignore
      questionIntegration.hasChanged = true

      // @ts-ignore
      setQuestion(question.id, {
        ...question,
        [`${integration}_integration`]: questionIntegration,
      })
    },
    [integration, questionIntegration, question, setQuestion]
  )

  const onSelectIntegrationField = React.useCallback(
    (selectedOption: string) => {
      questionIntegration.field = selectedOption
      // @ts-ignore
      questionIntegration.hasChanged = true

      // @ts-ignore
      setQuestion(question.id, {
        ...question,
        [`${integration}_integration`]: questionIntegration,
      })
    },
    [questionIntegration, setQuestion, question, integration]
  )

  React.useEffect(() => {
    if (questionType !== 'checkbox' && questionIntegrationField) {
      // @ts-ignore
      setQuestion(question.id, {
        ...question,
        [`${integration}_integration`]: {
          ...questionIntegration,
          is_list: false,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionType, integration])

  const onToggleIntegrationIsList = React.useCallback(() => {
    questionIntegration.is_list = !questionIntegration.is_list
    // @ts-ignore
    questionIntegration.hasChanged = true

    // @ts-ignore
    setQuestion(question.id, {
      ...question,
      [`${integration}_integration`]: questionIntegration,
    })
  }, [questionIntegration, question, setQuestion, integration])

  const onToggleIntegrationInverseMatrix = React.useCallback(() => {
    // @ts-ignore
    setQuestion(question.id, {
      ...question,
      inverse_matrix: !question?.inverse_matrix,
    })
  }, [question, setQuestion])

  const onToggleIntegrationIncludeCompletedDate = React.useCallback(() => {
    // @ts-ignore
    setQuestion(question.id, {
      ...question,
      include_completed_date: !question?.include_completed_date,
    })
  }, [question, setQuestion])

  const integrationTypeOptions = React.useMemo(
    // @ts-ignore
    () =>
      Object.keys(integrationOptions)
        // @ts-ignore
        .filter((e) => integrationOptions[e].length > 0)
        .map((e) => ({ label: e, value: e })),
    [integrationOptions]
  )

  const integrationFieldOptions = React.useMemo(() => {
    const integrationType = questionIntegrationField?.type

    if (!integrationType) return []

    return getIntegrationOptionsValue(integrationType, question)
  }, [questionIntegrationField?.type, getIntegrationOptionsValue, question])

  const removeIntegration = React.useCallback(() => {
    // @ts-ignore
    setQuestion(question.id, {
      ...question,
      [`${integration}_integration`]: null,
    })
  }, [question, setQuestion, integration])

  const { canEditPacketForm } = usePermissions()

  const removeButtonDisabled = React.useMemo(() => {
    if (!questionIntegrationField) return true

    if (!canEditPacketForm) return true

    return (
      // @ts-ignore
      questionIntegrationField?.isNew && !questionIntegrationField?.hasChanged
    )
  }, [canEditPacketForm, questionIntegrationField])

  if (!integration) return null

  return (
    <>
      <div className="mb-3 border-b pb-1">
        <button
          className={`relative top-1 float-right text-sm ${
            removeButtonDisabled ? 'text-red-200' : 'text-red-500'
          }`}
          disabled={removeButtonDisabled}
          onClick={removeIntegration}
        >
          Clear
        </button>
        <p className="font-medium text-txt-emphasis">
          Integration - {integrationTitledName}
        </p>
      </div>
      <Space>
        {/* @ts-ignore */}
        <Ui.Dropdown
          grayOutWhenDisabled
          disabled={!canEditPacketForm}
          label="Endpoint Category"
          onChange={onSelectIntegrationType}
          options={integrationTypeOptions}
          placeholder="Select an endpoint category"
          value={questionIntegration?.type}
          willShowNotAllowedCursor={!canEditPacketForm}
        />
        {/* @ts-ignore */}
        <Ui.Dropdown
          grayOutWhenDisabled
          disabled={!canEditPacketForm}
          label="Endpoint"
          onChange={onSelectIntegrationField}
          options={integrationFieldOptions}
          placeholder="Select an endpoint"
          value={questionIntegration?.field}
          willShowNotAllowedCursor={!canEditPacketForm}
        />
        {shouldShowLabel && (
          <TextField
            block
            grayOutWhenDisabled
            disabled={!canEditPacketForm}
            label="Label"
            // @ts-ignore
            onChange={onUpdateIntegrationLabel}
            placeholder="Integration label"
            value={questionIntegration?.field_label}
            willShowNotAllowedCursor={!canEditPacketForm}
          />
        )}
        {questionType === 'checkbox' && (
          <>
            <ToggleField
              horizontal
              // @ts-ignore
              primary
              disabled={!canEditPacketForm || !questionIntegration?.field}
              label="List Vertically"
              onChange={onToggleIntegrationIsList}
              value={Boolean(questionIntegration?.is_list)}
            />
          </>
        )}
        <ToggleField
          horizontal
          // @ts-ignore
          primary
          disabled={!canEditPacketForm || !questionIntegration?.field}
          label="Include Date Completed"
          onChange={onToggleIntegrationIncludeCompletedDate}
          value={Boolean(question?.include_completed_date)}
        />
        {questionType === 'matrix' &&
          // @ts-ignore
          ['radio', 'checkbox'].includes(questionSubtype) && (
            <ToggleField
              horizontal
              // @ts-ignore
              primary
              disabled={!canEditPacketForm || !questionIntegration?.field}
              label="Flip Matrix"
              onChange={onToggleIntegrationInverseMatrix}
              value={Boolean(question?.inverse_matrix)}
            />
          )}
      </Space>
    </>
  )
}

export default BuilderSideMenuIntegration
